import { useAuth0 } from "@auth0/auth0-react";
import { useFeatureGate } from "@statsig/react-bindings";
import { clientUserAuthGate } from "../../../statsig/gates";
import { Button, Divider, Text } from "@growtherapy/sprout-ui";
import { twMerge } from "tailwind-merge";
import { LoginExperience } from "../../../auth";

export type LoginSectionProps = {
  className?: string;
  isAuthenticated?: boolean;
  onLogin?: () => void;
};

function SectionDivider() {
  return (
    <div className="flex items-center justify-center w-full relative">
      <Divider className="absolute" use="secondary" />
      <Text className="text-neutral-700 bg-neutral-100 px-2 relative">
        Prefer not to log in?
      </Text>
    </div>
  );
}

function LoginCta({
  onLogin,
  className,
}: Pick<LoginSectionProps, "onLogin" | "className">) {
  return (
    <div
      className={twMerge("flex flex-col w-full gap-4 items-center", className)}
    >
      <Text className="font-medium text-center">
        Log in to access your shared session topics
      </Text>
      <Button className="w-full" onClick={onLogin}>
        Log in
      </Button>
    </div>
  );
}

export function LoginSection({
  className,
  isAuthenticated,
  onLogin,
}: LoginSectionProps) {
  if (isAuthenticated) return null;
  return (
    <div className="flex flex-col w-full gap-8">
      <LoginCta onLogin={onLogin} className={className} />
      <SectionDivider />
    </div>
  );
}

export function LoginSectionWrapper(
  props: Pick<LoginSectionProps, "className">,
) {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        target_login_experience: LoginExperience.CLIENT,
      },
      appState: { returnTo: location.pathname },
    });
  };
  const { value: isClientUserAuthEnabled } = useFeatureGate(clientUserAuthGate);

  if (!isClientUserAuthEnabled) return null;

  return (
    <LoginSection
      isAuthenticated={isAuthenticated}
      onLogin={handleLogin}
      {...props}
    />
  );
}
